import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Modal } from "react-bootstrap";
import { useParams } from "react-router-dom";
import cogoToast from "cogo-toast";
import { Card, CardBody, CardHeader, CardHeaderToolbar } from "../../../../_metronic/_partials/controls";
import {
    useGetFailedWithdrawalsRetryableQuery,
    useSearchFailedWithdrawalsQuery,
    useGetWithdrawalRetriesQuery,
    useRetryFailedWithdrawalMutation,
} from "../../../../redux/apiSlice";
import {
    errorMessage,
    formatAmount,
    formatDate,
    formatStatus,
    formatUsername,
    permissionAllow,
} from "../../../util/Helpers";
import { ViewIcon } from "../../../util/Icons";
import { Permissions, PermissionType } from "../../../util/Permissions";
import ViewPaymentModal from "../../UserManagement/pages/selected-user/view-user/user-pages/account-information/account-pages/payments/modal/ViewPaymentModal";

const PAGE_SIZE = 10;

const normalizeRows = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.rows)) return payload.rows;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.results)) return payload.results;
    if (Array.isArray(payload?.withdrawals)) return payload.withdrawals;
    return [];
};

const getTotalInfo = (payload) => {
    const candidates = [
        payload?.total,
        payload?.count,
        payload?.searchTotal,
        payload?.meta?.total,
        payload?.pagination?.total,
        payload?.pageInfo?.total,
    ];
    for (const candidate of candidates) {
        const numeric = Number(candidate);
        if (Number.isFinite(numeric)) return { total: numeric, known: true };
    }
    return { total: null, known: false };
};

const getPaymentRef = (row) => row?.paymentRef || row?.ref || row?.reference || row?.paymentReference || "-";
const getGatewayRef = (row) => row?.retryGatewayRef || row?.gatewayRef || row?.lastRetryResponse?.reference || "-";
const getRetryCount = (row) => row?.retryCount ?? row?.retries ?? row?.attempts ?? "-";
const getLastRetryAt = (row) => row?.lastRetryAt || row?.retriedAt || row?.retryDate || row?.updatedAt;
const getCreatedAt = (row) => row?.createdAt || row?.created || row?.updatedAt;
const getStatus = (row) => row?.status || row?.state || row?.retryStatus || row?.lastRetryResponse?.status || "-";
const getInitiatedBy = (row) => row?.initiatedBy || row?.retriedBy || row?.updatedBy || row?.processedBy || "-";
const getFullName = (row) =>
    row?.lastRetryResponse?.fullname
    || row?.meta?.full_name
    || row?.meta?.accountName
    || row?.fullname
    || row?.name
    || "-";
const getFailureMessage = (row) =>
    row?.lastRetryResponse?.complete_message
    || row?.meta?.complete_message
    || row?.lastRetryResponse?.message
    || row?.errorMessage
    || row?.failureReason
    || "-";

const formatMaybeAmount = (currency, value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return "-";
    return formatAmount(currency || "NGN", numericValue);
};

const getCurrency = (row) => row?.lastRetryResponse?.currency || row?.currency || "-";
const getAmount = (row) => row?.lastRetryResponse?.amount ?? row?.amount;
const getRetryableDisplayName = (row) =>
    row?.userId
        ? formatUsername(
            row.userId,
            row.username || row?.meta?.full_name || row?.meta?.accountName || [row.firstName, row.lastName].filter(Boolean).join(" "),
            null,
            row.accountId,
            "payments"
        )
        : getFullName(row);

const buildRetriedPaymentDetails = (row, userGroup) => {
    const amount = Number(getAmount(row) ?? 0);
    const fee = Number(row?.lastRetryResponse?.fee ?? 0);
    const accountNumber = row?.lastRetryResponse?.account_number || "";

    return {
        accountId: row?.accountId || null,
        amount,
        createdAt: getCreatedAt(row),
        currency: getCurrency(row),
        description: getFailureMessage(row),
        fwRef: row?.flutterwaveTransferId || row?.lastRetryResponse?.id || null,
        gateway: "flutterwave",
        gatewayRef: getGatewayRef(row),
        instantDeposit: false,
        instantFee: 0,
        ip: row?.ip || null,
        last4: accountNumber ? accountNumber.slice(-4) : null,
        meta: {
            ...row?.lastRetryResponse,
            retryCount: getRetryCount(row),
            retryGatewayRef: getGatewayRef(row),
            initiatedBy: getInitiatedBy(row),
            paymentRef: getPaymentRef(row),
        },
        method: row?.lastRetryResponse?.method || "NUBAN",
        processFee: fee,
        progress: [
            {
                date: getCreatedAt(row),
                status: "PROCESSING",
                activity: "Withdrawal retry initialized",
            },
            {
                date: getLastRetryAt(row) || row?.lastRetryResponse?.created_at || row?.updatedAt,
                status: getStatus(row),
                activity: getFailureMessage(row),
            },
        ].filter((entry) => entry.date),
        rate: 1,
        ref: getPaymentRef(row),
        saveCard: false,
        serviceFee: 0,
        status: getStatus(row),
        total: amount + fee,
        type: "WITHDRAW",
        updatedAt: getLastRetryAt(row) || row?.updatedAt,
        userGroup: row?.userGroup || userGroup || null,
        userId: row?.userId || row?.lastRetryResponse?.userId || null,
        username: getFullName(row),
    };
};

const resolveInitiatedBy = (adminUser) =>
    adminUser?.firstName
    || adminUser?.fullName
    || adminUser?.name
    || adminUser?.username
    || adminUser?.email
    || "Admin";

export const WithdrawalRetryTable = ({ mode }) => {
    const { userGroup } = useParams();
    const adminUser = useSelector((state) => state.auth.user);
    const [offset, setOffset] = useState(0);
    const [searchInput, setSearchInput] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
    const [viewPaymentDetails, setViewPaymentDetails] = useState(null);

    const queryArgs = useMemo(() => ({ offset, limit: PAGE_SIZE }), [offset]);
    const retryableQuery = useGetFailedWithdrawalsRetryableQuery(queryArgs, { skip: mode !== "retryable" });
    const retriedQuery = useGetWithdrawalRetriesQuery(queryArgs, { skip: mode !== "retried" });
    const searchQuery = useSearchFailedWithdrawalsQuery(searchTerm, { skip: !searchTerm });
    const activeQuery = searchTerm
        ? searchQuery
        : (mode === "retryable" ? retryableQuery : retriedQuery);
    const { data, isFetching, error, refetch } = activeQuery;

    const [retryFailedWithdrawal, { isLoading: isRetrying }] = useRetryFailedWithdrawalMutation();

    useEffect(() => {
        if (error) cogoToast.error(errorMessage(error));
    }, [error]);

    const rows = useMemo(() => normalizeRows(data), [data]);
    const totalInfo = useMemo(() => getTotalInfo(data), [data]);
    const canUpdatePayments = permissionAllow(adminUser, Permissions.PAYMENTS, PermissionType.UPDATE);
    const initiatedBy = resolveInitiatedBy(adminUser);
    const title = mode === "retryable" ? "Retryable Failed Withdrawals" : "Retried Withdrawals";
    const emptyState = mode === "retryable" ? "No failed withdrawals available for retry" : "No retried withdrawals found";
    const hasPrevious = !searchTerm && offset > 0;
    const hasNext = !searchTerm && (totalInfo.known ? (totalInfo.total > offset + rows.length) : (rows.length === PAGE_SIZE));
    const actionColSpan = mode === "retried" ? 9 : 8;
    const emptyStateText = searchTerm
        ? `No withdrawals found for "${searchTerm}"`
        : emptyState;

    const handleSearchSubmit = (event) => {
        event.preventDefault();
        const trimmedSearch = searchInput.trim();
        setOffset(0);
        setSearchTerm(trimmedSearch);
    };

    const clearSearch = () => {
        setSearchInput("");
        setSearchTerm("");
        setOffset(0);
    };

    const confirmRetry = async () => {
        const reference = getPaymentRef(selectedWithdrawal);
        if (!reference || reference === "-") return;

        try {
            await retryFailedWithdrawal({ ref: reference, initiatedBy }).unwrap();
            cogoToast.success("Withdrawal retry initiated");
            setSelectedWithdrawal(null);
            refetch();
        } catch (err) {
            cogoToast.error(errorMessage(err));
        }
    };

    return (
        <>
            <Card>
                <CardHeader title={title}>
                    <CardHeaderToolbar>
                        <div className="d-flex align-items-center flex-wrap">
                            <form className="d-flex align-items-center mr-3 mb-2 mb-md-0" onSubmit={handleSearchSubmit}>
                                <input
                                    type="text"
                                    className="form-control mr-2"
                                    style={{ minWidth: 260 }}
                                    value={searchInput}
                                    onChange={(event) => setSearchInput(event.target.value)}
                                    disabled={isFetching || isRetrying}
                                />
                                <Button
                                    type="submit"
                                    className="btn btn-primary mr-2"
                                    disabled={isFetching || isRetrying}
                                >
                                    Search
                                </Button>
                                {searchTerm ? (
                                    <Button
                                        type="button"
                                        className="btn btn-light"
                                        onClick={clearSearch}
                                        disabled={isFetching || isRetrying}
                                    >
                                        Clear
                                    </Button>
                                ) : null}
                            </form>
                            <Button
                                type="button"
                                className="btn btn-light-primary"
                                onClick={() => refetch()}
                                disabled={isFetching || isRetrying}
                            >
                                Refresh
                            </Button>
                        </div>
                    </CardHeaderToolbar>
                </CardHeader>
                <CardBody>
                    <div className="table-responsive">
                        <table className="table table-head-custom table-vertical-center">
                            <thead>
                                <tr>
                                    <th>Payment Ref</th>
                                    <th>{mode === "retryable" ? "User" : "Full Name"}</th>
                                    <th>Currency</th>
                                    <th>Amount</th>
                                    {mode === "retryable" ? null : <th>Retry Count</th>}
                                    <th>{mode === "retryable" ? "Created On" : "Last Retry"}</th>
                                    <th>Status</th>
                                    {mode === "retried" ? <th>Initiated By</th> : null}
                                    <th className="text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isFetching ? (
                                    <tr>
                                        <td colSpan={actionColSpan} className="text-center text-muted py-8">
                                            Loading...
                                        </td>
                                    </tr>
                                ) : rows.length ? (
                                    rows.map((row, index) => (
                                        <tr key={`${getPaymentRef(row)}-${index}`}>
                                            <td className="text-monospace">{getPaymentRef(row)}</td>
                                            <td>
                                                {mode === "retryable" ? getRetryableDisplayName(row) : getFullName(row)}
                                            </td>
                                            <td>{getCurrency(row)}</td>
                                            <td>{formatMaybeAmount(getCurrency(row), getAmount(row))}</td>
                                            {mode === "retryable" ? null : <td>{getRetryCount(row)}</td>}
                                            <td>{formatDate(mode === "retryable" ? getCreatedAt(row) : (getLastRetryAt(row) || getCreatedAt(row)))}</td>
                                            <td>{formatStatus(getStatus(row))}</td>
                                            {mode === "retried" ? (
                                                <td>{getInitiatedBy(row)}</td>
                                            ) : null}
                                            <td className="text-right">
                                                <div className="d-inline-flex align-items-center">
                                                    <Button
                                                        type="button"
                                                        className="btn btn-icon btn-light btn-hover-primary btn-sm bg-white"
                                                        onClick={() => {
                                                            if (mode === "retryable") {
                                                                setViewPaymentDetails(row);
                                                            } else {
                                                                setViewPaymentDetails(buildRetriedPaymentDetails(row, userGroup));
                                                            }
                                                        }}
                                                    >
                                                        <span className="svg-icon svg-icon-md">
                                                            <ViewIcon />
                                                        </span>
                                                    </Button>
                                                    {mode === "retryable" ? (
                                                        <Button
                                                            type="button"
                                                            className="btn btn-primary btn-sm ml-2"
                                                            disabled={!canUpdatePayments || isRetrying}
                                                            onClick={() => setSelectedWithdrawal(row)}
                                                        >
                                                            Retry
                                                        </Button>
                                                    ) : null}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={actionColSpan} className="text-center text-muted py-8">
                                            {emptyStateText}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex align-items-center justify-content-between mt-4">
                        <div className="text-muted">
                            Showing {rows.length} item{rows.length === 1 ? "" : "s"}
                            {totalInfo.known ? ` of ${totalInfo.total}` : ""}
                            {searchTerm ? ` for "${searchTerm}"` : ""}
                        </div>
                        <div className="d-flex align-items-center">
                            <Button
                                type="button"
                                className="btn btn-sm btn-light mr-2"
                                disabled={!hasPrevious || isFetching}
                                onClick={() => setOffset((prev) => Math.max(0, prev - PAGE_SIZE))}
                            >
                                Prev
                            </Button>
                            <div className="text-muted" style={{ minWidth: 120, textAlign: "center" }}>
                                {searchTerm ? "Search results" : `Page ${Math.floor(offset / PAGE_SIZE) + 1}`}
                            </div>
                            <Button
                                type="button"
                                className="btn btn-sm btn-light ml-2"
                                disabled={!hasNext || isFetching}
                                onClick={() => setOffset((prev) => prev + PAGE_SIZE)}
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                </CardBody>
            </Card>

            <Modal show={!!selectedWithdrawal} onHide={() => setSelectedWithdrawal(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Retry failed withdrawal</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-4">Are you sure you want to retry this failed withdrawal?</p>
                    {selectedWithdrawal ? (
                        <div className="table-responsive">
                            <table className="table table-sm mb-0">
                                <tbody>
                                    <tr>
                                        <th>Payment Ref</th>
                                        <td className="text-monospace">{getPaymentRef(selectedWithdrawal)}</td>
                                    </tr>
                                    <tr>
                                        <th>Amount</th>
                                        <td>{formatMaybeAmount(getCurrency(selectedWithdrawal), getAmount(selectedWithdrawal))}</td>
                                    </tr>
                                    <tr>
                                        <th>Initiated By</th>
                                        <td>{initiatedBy}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    ) : null}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={() => setSelectedWithdrawal(null)} disabled={isRetrying}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={confirmRetry} disabled={isRetrying || !canUpdatePayments}>
                        {isRetrying ? "Retrying..." : "Retry Withdrawal"}
                    </Button>
                </Modal.Footer>
            </Modal>

            {viewPaymentDetails ? (
                <ViewPaymentModal
                    showViewPaymentModal={!!viewPaymentDetails}
                    details={viewPaymentDetails}
                    onHide={() => setViewPaymentDetails(null)}
                />
            ) : null}
        </>
    );
};

export default WithdrawalRetryTable;
